import React, { Suspense, useCallback, useMemo, useReducer } from 'react'

import { StrokeOptions } from 'perfect-freehand'
import { createMemoryRouter } from 'react-router-dom'

import { InspectorContext } from '@cash-web/shared-data-access-redux-inspect'
import {
  cardStudioReducer,
  DEFAULT_DEVTOOLS_SETTINGS,
  DevToolsContext,
  enabledReducer,
  InitCardStudioDevToolsArgs,
  INITIAL_CARD_STUDIO_STATE,
  ToolKey,
} from '@cash-web/shared-devtools-data-access-context'

import { Performance } from './tools/Performance'

const canvasPerformance = new Performance()

// Shuts up type error
// eslint-disable-next-line react/jsx-no-useless-fragment
const Noop = ({ children }: { children?: React.ReactNode }) => <>{children}</>

const DevToolsWrapper =
  process.env['NODE_ENV'] === 'development' || process.env['NODE_ENV'] === 'test'
    ? React.lazy(() => import('./DevToolsWrapper'))
    : Noop

type DevToolsBoundaryProps = {
  journeyRouter?: ReturnType<typeof createMemoryRouter> | null
  children: React.ReactNode
}
const DevToolsBoundary = ({ journeyRouter, children }: DevToolsBoundaryProps) => {
  const [settings, dispatch] = useReducer(enabledReducer, DEFAULT_DEVTOOLS_SETTINGS)
  const [
    { imageData, showCoordinates, showGrid, context, metrics, strokeOptions, shouldCreatePreview },
    cardStudioDispatch,
  ] = useReducer(cardStudioReducer, INITIAL_CARD_STUDIO_STATE)

  const toggle = useCallback((key: ToolKey) => dispatch({ type: 'TOGGLE', key }), [dispatch])
  const initDevTools = useCallback(({ context, metrics }: InitCardStudioDevToolsArgs) => {
    cardStudioDispatch({ type: 'SET_CONTEXT', context })
    cardStudioDispatch({ type: 'SET_METRICS', metrics })
  }, [])
  const togglePreview = useCallback(
    (value: boolean) => cardStudioDispatch({ type: 'TOGGLE_PREVIEW', value }),
    [cardStudioDispatch]
  )
  const updateDevToolsImageData = useCallback(
    (imageData: string) => {
      cardStudioDispatch({ type: 'UPDATE_IMAGE_DATA', imageData })
    },
    [cardStudioDispatch]
  )
  const toggleOption = useCallback(
    (key: 'showGrid' | 'showCoordinates') => cardStudioDispatch({ type: 'TOGGLE_CARD_STUDIO_OPTION', key }),
    [cardStudioDispatch]
  )
  const setStrokeOptions = useCallback(
    (strokeOptions: StrokeOptions) => cardStudioDispatch({ type: 'SET_STROKE_OPTIONS', strokeOptions }),
    [cardStudioDispatch]
  )
  const withPerformanceMetrics = useCallback(
    (drawFn: () => void, canvasName: string) => {
      if (settings.cardStudioTools) {
        performance.mark(`${canvasName} start`)
        canvasPerformance.begin()
      }

      drawFn()

      if (settings.cardStudioTools) {
        canvasPerformance.end()
        performance.mark(`${canvasName} end`)
        performance.measure(canvasName, `${canvasName} start`, `${canvasName} end`)
      }
    },
    [settings.cardStudioTools]
  )

  const devTools = useMemo(
    () => ({
      journeyRouter,
      settings,
      toggle,
      cardStudio: {
        context,
        metrics,
        imageData,
        showCoordinates,
        showGrid,
        initDevTools,
        updateDevToolsImageData,
        toggleOption,
        strokeOptions,
        setStrokeOptions,
        performance: canvasPerformance,
        withPerformanceMetrics,
        togglePreview,
        shouldCreatePreview,
      },
    }),
    [
      journeyRouter,
      settings,
      toggle,
      context,
      metrics,
      imageData,
      showCoordinates,
      showGrid,
      initDevTools,
      updateDevToolsImageData,
      toggleOption,
      strokeOptions,
      setStrokeOptions,
      withPerformanceMetrics,
      togglePreview,
      shouldCreatePreview,
    ]
  )
  const inspectorContext = useMemo(() => ({ enabled: settings.reactReduxTools }), [settings.reactReduxTools])

  return (
    <DevToolsContext.Provider value={devTools}>
      <InspectorContext.Provider value={inspectorContext}>{children}</InspectorContext.Provider>
      <Suspense>
        <DevToolsWrapper settings={settings} toggle={toggle} />
      </Suspense>
    </DevToolsContext.Provider>
  )
}

export const DevTools =
  process.env['NODE_ENV'] === 'development' || process.env['NODE_ENV'] === 'test' ? DevToolsBoundary : Noop
